import { z } from 'zod';

export const provincesEnum = z.enum([
  'MB',
  'ON',
  'BC',
  'AB',
  'SK',
  'NS',
  'NB',
  'NL',
  'PE',
  'QC',
  'YT',
  'NT',
  'NU',
]);

export type Province = z.infer<typeof provincesEnum>;

export const billingNumberFormatSchema = z.enum([
  'ANY',
  'NUMERIC',
  'ALPHANUMERIC',
]);

export type BillingNumberFormat = z.infer<typeof billingNumberFormatSchema>;

export interface BillingNumberValidationRules {
  billingNumberMinLength?: number | null;
  billingNumberMaxLength?: number | null;
  billingNumberFormat?: BillingNumberFormat | null;
}

interface BillingNumberLicense {
  payorID: string;
  license: string;
}

interface BillingNumberPayor extends BillingNumberValidationRules {
  id: string;
}

export type BillingNumberValidationIssue =
  | { code: 'minLength'; length: number }
  | { code: 'maxLength'; length: number }
  | { code: 'numeric' }
  | { code: 'alphanumeric' };

export const validateBillingNumber = (
  rules: BillingNumberValidationRules,
  billingNumber: string
): BillingNumberValidationIssue | null => {
  const normalizedBillingNumber = billingNumber.trim();
  const minLength = rules.billingNumberMinLength;
  const maxLength = rules.billingNumberMaxLength;

  if (minLength != null && normalizedBillingNumber.length < minLength) {
    return { code: 'minLength', length: minLength };
  }

  if (maxLength != null && normalizedBillingNumber.length > maxLength) {
    return { code: 'maxLength', length: maxLength };
  }

  if (
    rules.billingNumberFormat === 'NUMERIC' &&
    !/^\d+$/.test(normalizedBillingNumber)
  ) {
    return { code: 'numeric' };
  }

  if (
    rules.billingNumberFormat === 'ALPHANUMERIC' &&
    !/^[A-Za-z0-9]+$/.test(normalizedBillingNumber)
  ) {
    return { code: 'alphanumeric' };
  }

  return null;
};

export const hasInvalidBillingNumbers = (
  licenses: BillingNumberLicense[],
  payors: BillingNumberPayor[]
): boolean => {
  const payorsByID = new Map(payors.map((payor) => [payor.id, payor]));

  return licenses.some((license) => {
    const payor = payorsByID.get(license.payorID);
    return !payor || !!validateBillingNumber(payor, license.license);
  });
};

export const payorSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(['PRIVATE', 'PUBLIC', 'DIRECT']),
  fax: z.string().nullable().default(null),
  province: provincesEnum.nullable().default(null),
  phone: z.string().nullable().default(null),
  email: z.string().nullable().default(null),
  note: z.string().nullable().default(null),
  billingNumberMinLength: z
    .number()
    .int()
    .min(1)
    .max(255)
    .nullable()
    .optional(),
  billingNumberMaxLength: z
    .number()
    .int()
    .min(1)
    .max(255)
    .nullable()
    .optional(),
  billingNumberFormat: billingNumberFormatSchema.optional(),
});

export const payorSummarySchema = payorSchema
  .pick({
    id: true,
    name: true,
    type: true,
  })
  .strict();
